/**
 * Last-touch trade attribution: which social surface sent the user to the
 * event page they're trading on. Captured from `ref_source`/`ref_id` URL
 * params by `useTradeAttributionCapture`, read at order-submit time by the
 * order analytics (`sendTradeAnalytics`, `useOrderStepBaseProps`).
 *
 * Semantics: last-touch, event-scoped, 30-minute window, per-tab
 * (sessionStorage persistence survives reloads but not new tabs).
 */
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

export interface TradeAttribution {
    source: 'social_post' | 'social_activity';
    post_id: string;
    /** Event page the attribution was captured on. */
    event_ticker: string;
    /** Epoch ms at capture time. */
    captured_at: number;
}

export const ATTRIBUTION_TTL_MS = 30 * 60 * 1000;

interface TradeAttributionState {
    attribution: TradeAttribution | null;
    actions: {
        attributionCaptured: (attribution: TradeAttribution) => void;
    };
}

export const useTradeAttributionStore = create<TradeAttributionState>()(
    persist(
        (set) => ({
            attribution: null,
            actions: {
                attributionCaptured: (attribution) => set({ attribution }),
            },
        }),
        {
            name: 'Kalshi-TradeAttribution',
            storage: createJSONStorage(() => sessionStorage),
            partialize: (state) => ({ attribution: state.attribution }),
        },
    ),
);

/**
 * Attach-time guard: returns the attribution only while it's fresh and it
 * matches the event being traded. Callers that don't know the event get null
 * — under-attributing beats inheriting a stale social touch on an unrelated
 * trade.
 */
export function getActiveTradeAttribution(eventTicker: string | undefined): TradeAttribution | null {
    if (!eventTicker) return null;
    const attribution = useTradeAttributionStore.getState().attribution;
    if (!attribution) return null;
    if (Date.now() - attribution.captured_at > ATTRIBUTION_TTL_MS) return null;
    if (attribution.event_ticker !== eventTicker) return null;
    return attribution;
}
